import { Injectable } from '@angular/core';
import { CollectionPaths } from '@models/collections';
import {
	defaultMatterPackage,
	MatterPackage,
	MatterPackageContent,
	MatterPackageContentType,
	MatterPackageContentDocumentType,
	PackageHistoryAction,
} from '@models/matter-package';
import { MatterWordDocument } from '@models/matter-word-documents';
import { MatterWordDocumentWithMetadata } from '../matter-word-documents/matter-word-documents.selectors';
import {
	Action,
	dispatch,
	select,
	Selector,
	State,
	StateContext,
} from '@ngxs/store';
import { RecursiveKeyOf } from '@repos/firestore-repo.service';
import { MatterPackageHistoryService } from '@services/matter-package-history.service';
import { MatterPackageEnvelopeService } from '@services/matter-package-envelope.service';
import { MatterPackagesService } from '@services/matter-packages/matter-packages.service';
import { NavigationService } from '@services/nav/nav.service';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessState } from '@state/business/business.state';
import { FileDownloadDispatch } from '@state/file-download/fileDownload.dispatch';
import { FileDownloadType } from '@state/file-download/fileDownload.model';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { FirestoreStateBaseForMatterWithSubscription } from '@state/FirestoreStateBaseForMatter';
import { CreateStatusType } from '@state/utils/models';
import { FieldValue } from 'firebase/firestore/lite';
import { ToastService } from 'src/app/ui/toast/toast.service';
import {
	Create as CreateWordDoc,
	CreateBatch as CreateWordDocsBatch,
} from '../matter-word-documents/matter-word-documents.actions';
import * as actions from './matter-packages.actions';
import {
	MATTER_PACKAGES_STATE_TOKEN,
	MatterPackagesStateModel,
} from './matter-packages.state.model';
import * as matterPackageHistoryActions from '../matter-packages-history/matter-packages-history.actions';
import * as matterUploadFileActions from '../uploaded-files/matter-uploaded-files.actions';
import { createId } from 'src/app/shared/core/util/file';
import { Logger } from 'src/app/shared/core/logger';
import { firstValueFrom } from 'rxjs';
import { SignItApiService } from 'src/app/shared/api-repos/signit/signit.api.service';

type Ctx = StateContext<MatterPackagesStateModel>;

const DEFAULT_STATE: MatterPackagesStateModel = {
	...DEFAULT_STATE_BASE,
	selectedId: null,
	createStatus: null,
	lastCreatedPackageId: null,
	items: [],
	exportingPackageIds: [],
};

@State<MatterPackagesStateModel>({
	name: MATTER_PACKAGES_STATE_TOKEN,
	defaults: DEFAULT_STATE,
})
@Injectable({
	providedIn: 'root',
})
export class MatterPackagesState extends FirestoreStateBaseForMatterWithSubscription<
	MatterPackage,
	MatterPackagesStateModel
> {
	matterPackageHistoryDispatch = dispatch(matterPackageHistoryActions.Create);

	private loggedInUser_ = select(BusinessUsersState.getLoggedInBusinessUser);
	private isSignItSelected = select(BusinessState.isSignITSelected);

	@Selector([MatterPackagesState])
	static getAllPackages(state: MatterPackagesStateModel): MatterPackage[] {
		return state?.items;
	}

	@Selector([MatterPackagesState])
	static getPackageById(
		state: MatterPackagesStateModel,
	): (id: string) => MatterPackage {
		return (id: string) => {
			return state.items.find((item) => item.id === id);
		};
	}

	@Selector()
	static getSelectedId(state: MatterPackagesStateModel): string {
		return state.selectedId;
	}

	@Selector()
	static getCreateStatus(state: MatterPackagesStateModel): CreateStatusType {
		return state.createStatus;
	}

	@Selector([MatterPackagesState])
	static getCurrentPackageId(state: MatterPackagesStateModel): string {
		return state.selectedId;
	}

	@Selector([MatterPackagesState])
	static getExportingPackageIds(state: MatterPackagesStateModel): string[] {
		return state.exportingPackageIds;
	}

	constructor(
		private navService: NavigationService,
		private fileDownloadDispatch: FileDownloadDispatch,
		private toastService: ToastService,
		private matterPackageHistoryService: MatterPackageHistoryService,
		private matterPackageEnvelopeService: MatterPackageEnvelopeService,
		private matterPackagesService: MatterPackagesService,
		private signItApiService: SignItApiService,
	) {
		// Note: Cannot inject MatterPackagesSelectors here due to circular dependency
		// Will use store.selectSnapshot instead
		super(
			(pathParams) =>
				CollectionPaths.business_matter_packages(
					pathParams.businessId,
					pathParams.matterId,
				),
			(payload) => ({ items: payload as MatterPackage[] }),
			() => DEFAULT_STATE,
		);
	}

	@Action(actions.SelectPackage)
	selectPackage(ctx: Ctx, { payload }: actions.SelectPackage): void {
		ctx.patchState({ selectedId: payload.id });
		// Set the current package ID in history state when selecting a package

		ctx.dispatch(
			new matterPackageHistoryActions.SetCurrentPackageId({
				currentPackageId: payload.id,
			}),
		);
	}

	@Action(actions.Create)
	async set(ctx: Ctx, { payload }: actions.Create): Promise<void> {
		ctx.patchState({ createStatus: CreateStatusType.creating });
		try {
			//ToDo: when creating a package inside a matter no need to hash id, but when creating a package coming from the package template the id should be hashed using packageTemplateId and matterId;
			// Create package contents from documents if provided
			const contents: MatterPackageContent[] = [];
			if (payload.documents?.length > 0) {
				payload.documents.forEach((doc, index) => {
					contents.push({
						id: doc.id,
						contentType: doc.contentType || MatterPackageContentType.MATTER_DOC,
						documentType: MatterPackageContentDocumentType.WORD,
						ordinal: index + 1,
						title: doc.document.title,
					});
				});
			}

			const id = payload.id || createId(20);

			await this.repo.CreateOrMerge(id, {
				...defaultMatterPackage,
				packageName: payload.packageName,
				createdAt: Date.now(),
				createdBy: this.loggedInUser_().id,
				lastUpdatedBy: this.loggedInUser_().id,
				contents,
				packageTemplateId: payload.packageTemplateId,
				packageHash: payload.packageHash,
			});

			if (this.isSignItSelected()) {
				try {
					await firstValueFrom(
						this.signItApiService.createDraftEnvelope(
							this.matterId,
							id,
							payload.packageName,
						),
					);
				} catch (error) {
					Logger.error(
						`Failed to create draft envelope for package ${id}: ${error}`,
					);
				}
			}

			// If documents were provided, create word documents in batch
			if (payload.documents?.length > 0) {
				const documentsToCreate = payload.documents
					.filter(
						(doc) => doc.contentType === MatterPackageContentType.MATTER_DOC,
					)
					.map((doc) => {
						const result =
							(doc as MatterWordDocumentWithMetadata).document || doc;
						return result;
					})
					.filter((doc) => doc) as MatterWordDocument[];
				ctx.dispatch(new CreateWordDocsBatch(documentsToCreate, false));
			}

			// Only open drawer if package is not created from a template
			if (!payload.packageTemplateId) {
				localStorage.setItem('matter-packages-open-drawer', 'true');
			}

			ctx.patchState({
				selectedId: id,
				lastCreatedPackageId: id,
				createStatus: CreateStatusType.success,
			});

			ctx.dispatch(
				new matterPackageHistoryActions.Create({
					packageId: id,
					entry: {
						userId: this.loggedInUser_().id,
						action: PackageHistoryAction.CREATED,
						timestamp: Date.now(),
						contentId: id,
						contentTitle: payload.packageName,
					},
				}),
			);

			// Add history entries for each document that was added in batch
			if (payload.documents?.length > 0) {
				const historyEntries = payload.documents.map((doc, index) => ({
					userId: this.loggedInUser_().id,
					action: PackageHistoryAction.CONTENT_ADDED,
					timestamp: Date.now() + index, // Add small increment to ensure unique timestamps
					contentType: MatterPackageContentType.MATTER_DOC,
					contentId: doc.id,
					contentTitle: doc.document.title,
					documentType: MatterPackageContentDocumentType.WORD,
				}));

				ctx.dispatch(
					new matterPackageHistoryActions.CreateBatch({
						packageId: id,
						entries: historyEntries,
					}),
				);
			}

			this.navService.goToMatter_Packages_Package(this.matterId, id);
		} catch {
			ctx.patchState({
				createStatus: CreateStatusType.failed,
				lastCreatedPackageId: null,
			});
		}
	}

	@Action(actions.AddContent)
	async addContent(
		ctx: Ctx,
		{ docMetadata, duplicated, generated }: actions.AddContent,
	): Promise<void> {
		if (
			docMetadata.contentType === MatterPackageContentDocumentType.WORD &&
			docMetadata.type !== MatterPackageContentType.UPLOADED_FILE &&
			docMetadata.type !== MatterPackageContentType.SYNC_FORM &&
			docMetadata.type !== MatterPackageContentType.MATTER_FORM &&
			!generated
		) {
			// Wait for the document to be added first. If not awaited a blank content will be added to the package.
			await firstValueFrom(
				ctx.dispatch(
					new CreateWordDoc(
						docMetadata.content as MatterWordDocument,
						duplicated,
					),
				),
			);
		}

		const pkgContentLength =
			ctx.getState().items.find((p) => p.id === docMetadata.id)?.contents
				?.length ?? 0;
		const contentToAdd: MatterPackageContent = {
			id: docMetadata.content.id,
			contentType: docMetadata.type,
			ordinal: pkgContentLength + 1,
			documentType: docMetadata.contentType,
			title: docMetadata.title,
		};

		await this.repo.AddToArray(docMetadata.id, 'contents', contentToAdd);

		ctx.dispatch(
			new matterPackageHistoryActions.Create({
				packageId: docMetadata.id,
				entry: {
					userId: this.loggedInUser_().id,
					action: PackageHistoryAction.CONTENT_ADDED,
					timestamp: Date.now(),
					contentType: docMetadata.type,
					contentId: docMetadata.content.id,
					contentTitle: docMetadata.title,
					documentType: docMetadata.contentType,
				},
			}),
		);

		// Track user customization - user manually added content
		ctx.dispatch(
			new actions.TrackCustomization({
				packageId: docMetadata.id,
				action: 'add',
				documentId: docMetadata.content.id,
			}),
		);
	}

	@Action(matterUploadFileActions.Delete)
	async deleteUploadedContentFromPackages(
		ctx: Ctx,
		{ id }: matterUploadFileActions.Delete,
	) {
		const newPackagesWithoutUploadedContent = ctx
			.getState()
			.items.reduce((acc, item) => {
				let shouldUpdate = false;
				const newItem = {
					...item,
					contents: item.contents?.filter((content) => {
						if (!shouldUpdate && content.id === id) {
							shouldUpdate = true;
						}
						return (
							content.id !== id ||
							(content.contentType !== MatterPackageContentType.UPLOADED_FILE &&
								content.contentType !== MatterPackageContentType.SYNC_FORM &&
								content.contentType !== MatterPackageContentType.MATTER_FORM)
						);
					}),
				};

				return shouldUpdate ? acc.concat(newItem) : acc;
			}, []);

		if (newPackagesWithoutUploadedContent.length) {
			const batch = this.repo.Batch();
			newPackagesWithoutUploadedContent.forEach((item) => {
				batch.set(
					this.repo.DocRef(item.id),
					{ contents: item.contents },
					{ merge: true },
				);
			});
			await batch.commit();
		}
	}
	@Action(actions.RemoveContent)
	async removeContent(
		ctx: Ctx,
		{ payload }: actions.RemoveContent,
	): Promise<void> {
		const pkg = ctx.getState().items.find((p) => p.id === payload.packageId);

		if (!pkg?.contents) {
			return;
		}

		const contentToRemove = pkg.contents.find(
			(content) => content.id === payload.contentId,
		);

		if (!contentToRemove) {
			return;
		}

		const filteredContents = pkg.contents.filter(
			(content) => content.id !== payload.contentId,
		);

		// Create new array with updated ordinals
		const updatedContents = filteredContents.map((content, index) => ({
			...content,
			ordinal: index + 1,
		}));

		await this.repo.CreateOrMerge(payload.packageId, {
			contents: updatedContents,
		});

		ctx.dispatch(
			new matterPackageHistoryActions.Create({
				packageId: payload.packageId,
				entry: {
					userId: this.loggedInUser_().id,
					action: PackageHistoryAction.CONTENT_REMOVED,
					timestamp: Date.now(),
					contentId: payload.contentId,
					contentTitle: contentToRemove.title,
				},
			}),
		);

		// Track user customization - user manually removed content
		ctx.dispatch(
			new actions.TrackCustomization({
				packageId: payload.packageId,
				action: 'remove',
				documentId: payload.contentId,
			}),
		);
	}

	@Action(actions.UpdatePackageName)
	async updatePackageName(
		ctx: Ctx,
		{ payload }: actions.UpdatePackageName,
	): Promise<void> {
		await this.repo.OverwriteFields(
			payload.id,
			'packageName',
			payload.packageName,
		);

		ctx.dispatch(
			new matterPackageHistoryActions.Create({
				packageId: payload.id,
				entry: {
					userId: this.loggedInUser_().id,
					action: PackageHistoryAction.NAME_UPDATED,
					timestamp: Date.now(),
					contentTitle: payload.packageName,
				},
			}),
		);
	}

	@Action(actions.UpdateFieldValue)
	setFieldValue(ctx: Ctx, { payload }: actions.UpdateFieldValue): void {
		this.repo.OverwriteFields(
			payload.id,
			payload.fieldName as RecursiveKeyOf<MatterPackage>,
			payload.value as FieldValue,
		);
	}

	@Action(actions.ReorderContent)
	async reorderContent(
		ctx: Ctx,
		{ payload }: actions.ReorderContent,
	): Promise<void> {
		const state = ctx.getState();
		const pkg = state.items.find((p) => p.id === payload.id);
		if (!pkg) return;
		const contents = [...pkg.contents];
		const oldIndex = contents.findIndex((c) => c.id === payload.contentId);
		if (oldIndex === -1) return;
		let adjustedNewIndex = payload.newIndex;
		if (oldIndex < payload.newIndex) {
			adjustedNewIndex = Math.min(adjustedNewIndex, contents.length - 1);
		}
		const [item] = contents.splice(oldIndex, 1);
		if (payload.newIndex >= contents.length) {
			contents.push(item);
		} else {
			contents.splice(adjustedNewIndex, 0, item);
		}

		const updatedContents = contents.map((c, index) => ({
			...c,
			ordinal: index + 1,
		}));
		await this.repo.OverwriteFields(payload.id, 'contents', updatedContents);

		ctx.dispatch(
			new matterPackageHistoryActions.Create({
				packageId: payload.id,
				entry: {
					userId: this.loggedInUser_().id,
					action: PackageHistoryAction.REORDERED,
					timestamp: Date.now(),
					contentId: payload.contentId,
				},
			}),
		);
	}

	@Action(actions.DeletePackage)
	async deletePackage(ctx: Ctx, { payload }: actions.DeletePackage) {
		try {
			await this.deleteSubcollectionsForPackage(payload);

			await this.repo.Delete(payload.id);

			this.toastService.success('Package deleted successfully');
		} catch (error) {
			console.error('Failed to delete package:', error);
			this.toastService.error('Failed to delete package');
		}
	}

	private async deleteSubcollectionsForPackage(payload: { id: string }) {
		await Promise.all([
			this.matterPackageHistoryService.deleteHistoryForPackage(payload.id),
			this.matterPackageEnvelopeService.deleteEnvelopesForPackage(payload.id),
		]);
	}

	@Action(actions.ExportPackage)
	async exportPackage(
		ctx: Ctx,
		{ packageId }: actions.ExportPackage,
	): Promise<void> {
		const pkg = ctx.getState().items.find((p) => p.id === packageId);
		if (!pkg || !pkg.contents?.length) {
			this.toastService.error('No documents found in package');
			return;
		}

		ctx.patchState({
			exportingPackageIds: [...ctx.getState().exportingPackageIds, packageId],
		});

		try {
			const mergedPdf =
				await this.matterPackagesService.getPackageAsPdfBlob(packageId);

			ctx.dispatch(
				new matterPackageHistoryActions.Create({
					packageId: packageId,
					entry: {
						userId: this.loggedInUser_().id,
						action: PackageHistoryAction.EXPORTED,
						timestamp: Date.now(),
					},
				}),
			);

			this.fileDownloadDispatch.DownloadFile({
				name: pkg.packageName,
				type: 'pdf',
				data: mergedPdf,
				dataType: FileDownloadType.BLOB,
				byPassFileNaming: true,
			});

			this.toastService.success('Package exported successfully');
		} catch (error) {
			this.toastService.error('Failed to export package');
			Logger.exception(error, { context: 'MatterPackagesState.exportPackage' });
		} finally {
			ctx.patchState({
				exportingPackageIds: ctx
					.getState()
					.exportingPackageIds.filter((id) => id !== packageId),
			});
		}
	}

	@Action(actions.TrackCustomization)
	async trackCustomization(
		ctx: Ctx,
		{ payload }: actions.TrackCustomization,
	): Promise<void> {
		const state = ctx.getState();
		const pkg = state.items.find((p) => p.id === payload.packageId);

		if (!pkg) {
			return;
		}

		const currentCustomizations = pkg.customizations || {
			addedDocuments: [],
			removedDocuments: [],
		};

		const updatedCustomizations = { ...currentCustomizations };

		if (payload.action === 'add') {
			// Track that user manually added this document
			if (!updatedCustomizations.addedDocuments.includes(payload.documentId)) {
				updatedCustomizations.addedDocuments = [
					...updatedCustomizations.addedDocuments,
					payload.documentId,
				];
			}
			// Remove from removedDocuments if it was previously there
			updatedCustomizations.removedDocuments =
				updatedCustomizations.removedDocuments.filter(
					(id) => id !== payload.documentId,
				);
		} else if (payload.action === 'remove') {
			// Track that user manually removed this document
			if (
				!updatedCustomizations.removedDocuments.includes(payload.documentId)
			) {
				updatedCustomizations.removedDocuments = [
					...updatedCustomizations.removedDocuments,
					payload.documentId,
				];
			}
			// Remove from addedDocuments if it was previously there
			updatedCustomizations.addedDocuments =
				updatedCustomizations.addedDocuments.filter(
					(id) => id !== payload.documentId,
				);
		}

		await this.repo.OverwriteFields(
			payload.packageId,
			'customizations',
			updatedCustomizations,
		);
	}

	@Action(actions.SyncPackageContents)
	async syncPackageContents(
		ctx: Ctx,
		{ payload }: actions.SyncPackageContents,
	): Promise<void> {
		const state = ctx.getState();
		const matterPkg = state.items.find((p) => p.id === payload.packageId);

		if (!matterPkg) {
			console.warn(
				`Package ${payload.packageId} not found for synchronization`,
			);
			return;
		}

		const customizations = payload.customizations || {
			addedDocuments: [],
			removedDocuments: [],
		};

		// Create maps for efficient lookup
		const virtualDocIds = new Set(
			payload.documents.map((doc) => doc.document.id),
		);
		const virtualContentMap = new Map(
			payload.contents.map((content) => [content.id, content]),
		);
		const matterDocIds = new Set(
			matterPkg.contents?.map((content) => content.id) || [],
		);
		const addedByUserIds = new Set(customizations.addedDocuments);
		const removedByUserIds = new Set(customizations.removedDocuments);

		// Find documents to add: in virtual but not in matter, and not explicitly removed by user
		const docsToAdd = payload.documents.filter(
			(doc) =>
				!matterDocIds.has(doc.document.id) &&
				!removedByUserIds.has(doc.document.id),
		);

		// Find documents to remove: in matter but not in virtual, and not explicitly added by user
		// Also remove documents that were explicitly removed by user
		const contentsToKeep = (matterPkg.contents || []).filter((content) => {
			// If user explicitly removed it, don't keep it
			if (removedByUserIds.has(content.id)) {
				return false;
			}
			// Keep it if it's in virtual or was added by user
			return virtualDocIds.has(content.id) || addedByUserIds.has(content.id);
		});

		// Combine and reorder: start with kept contents, then add new ones
		const updatedContents = [
			...contentsToKeep,
			...docsToAdd.map((doc) => {
				const virtualContent = virtualContentMap.get(doc.document.id);
				return {
					id: doc.document.id,
					contentType:
						virtualContent?.contentType || MatterPackageContentType.MATTER_DOC,
					documentType:
						virtualContent?.documentType ||
						MatterPackageContentDocumentType.WORD,
					ordinal: 0, // Will be set below
					title: doc.document.title,
				};
			}),
		].map((content, index) => ({
			...content,
			ordinal: index + 1,
		}));

		// If documents need to be created (they don't exist in the matter yet)
		if (docsToAdd.length > 0) {
			const documentsToCreate = docsToAdd
				.map((doc) => {
					const virtualContent = virtualContentMap.get(doc.document.id);
					const contentType =
						virtualContent?.contentType || MatterPackageContentType.MATTER_DOC;

					// Only create actual word documents
					if (contentType === MatterPackageContentType.MATTER_DOC) {
						return doc.document;
					}
					return null;
				})
				.filter((doc) => doc !== null) as MatterWordDocument[];

			if (documentsToCreate.length > 0) {
				ctx.dispatch(new CreateWordDocsBatch(documentsToCreate, false));
			}
		}

		// Update the package with new contents and customizations
		const updatedCustomizations = {
			...customizations,
			lastSyncedVirtualHash: payload.packageId, // Track that we've synced with this virtual package
		};

		await this.repo.CreateOrMerge(payload.packageId, {
			contents: updatedContents,
			customizations: updatedCustomizations,
			lastUpdatedAt: Date.now(),
			lastUpdatedBy: 'system-sync',
		});

		// Create history entries for documents that were added
		if (docsToAdd.length > 0) {
			const historyEntries = docsToAdd.map((doc, index) => {
				const virtualContent = virtualContentMap.get(doc.document.id);
				return {
					userId: 'system-sync',
					action: PackageHistoryAction.CONTENT_ADDED,
					timestamp: Date.now() + index, // Small increment for unique timestamps
					contentType:
						virtualContent?.contentType || MatterPackageContentType.MATTER_DOC,
					contentId: doc.document.id,
					contentTitle: doc.document.title,
					documentType:
						virtualContent?.documentType ||
						MatterPackageContentDocumentType.WORD,
				};
			});

			ctx.dispatch(
				new matterPackageHistoryActions.CreateBatch({
					packageId: payload.packageId,
					entries: historyEntries,
				}),
			);
		}

		// Create history entries for documents that were removed
		const removedDocIds = Array.from(matterDocIds).filter(
			(id) => !virtualDocIds.has(id) && !addedByUserIds.has(id),
		);

		if (removedDocIds.length > 0) {
			const removedHistoryEntries = removedDocIds.map((docId, index) => {
				const removedContent = matterPkg.contents?.find((c) => c.id === docId);
				return {
					userId: 'system-sync',
					action: PackageHistoryAction.CONTENT_REMOVED,
					timestamp: Date.now() + docsToAdd.length + index,
					contentId: docId,
					contentTitle: removedContent?.title || 'Unknown',
				};
			});

			ctx.dispatch(
				new matterPackageHistoryActions.CreateBatch({
					packageId: payload.packageId,
					entries: removedHistoryEntries,
				}),
			);
		}
	}

	private syncedKeys = new Set<string>();

	@Action(actions.SyncPackageWithVirtual)
	async syncPackageWithVirtual(
		ctx: Ctx,
		{ payload }: actions.SyncPackageWithVirtual,
	): Promise<void> {
		const state = ctx.getState();
		const matterPkg = state.items.find((p) => p.id === payload.matterPackageId);

		if (!matterPkg) {
			console.warn(
				`Matter package ${payload.matterPackageId} not found for synchronization`,
			);
			return;
		}

		// Skip packages without a packageHash (not created from templates)
		if (!matterPkg.packageHash) {
			return;
		}

		// If virtual package data wasn't provided, we can't sync
		if (
			!payload.virtualDocuments ||
			!payload.virtualContents ||
			!payload.virtualPackageHash
		) {
			console.log(
				`No virtual package data provided for ${matterPkg.packageName}`,
			);
			return;
		}

		// Calculate what the matter package SHOULD look like after applying customizations
		const matterDocIds = new Set(
			matterPkg.contents?.map((content) => content.id) || [],
		);
		const virtualDocIds = payload.virtualDocuments
			.map((doc) => doc.id)
			.filter((id) => !matterPkg.customizations?.removedDocuments.includes(id));
		const addedDocIds = matterPkg.customizations?.addedDocuments || [];
		const expectedDocIds = new Set([...virtualDocIds, ...addedDocIds]);

		// Create a sync key based on the expected final state
		const syncKey = `${matterPkg.id}:${[...expectedDocIds].sort().join(',')}`;

		// Check if documents are already in sync
		const areDocListsEqual =
			matterDocIds.size === expectedDocIds.size &&
			[...matterDocIds].every((id) => expectedDocIds.has(id));

		// Only sync if documents don't match AND we haven't already synced to this state
		if (areDocListsEqual) {
			return;
		}

		if (this.syncedKeys.has(syncKey)) {
			return;
		}

		// Mark this state as synced before performing the sync
		this.syncedKeys.add(syncKey);

		// Perform the sync
		ctx.dispatch(
			new actions.SyncPackageContents({
				packageId: matterPkg.id,
				contents: payload.virtualContents,
				documents: payload.virtualDocuments as MatterWordDocumentWithMetadata[],
				customizations: matterPkg.customizations || {
					addedDocuments: [],
					removedDocuments: [],
				},
			}),
		);
	}
}
